import { ComponentType, IConfig } from '../../types';
import {
  BASIC_JSON_LOGIC_TOOLTIP,
  BASIC_SELECT_VALUE_TOOLTIP,
  BASIC_SWITCH_VALUE_TOOLTIP,
  BASIC_TANSLATE_TOOLTIP,
  BASIC_VALUE_TOOLTIP,
  BASIC_VARIABLE_TOOLTIP,
  ComponentConfigType,
} from '../../utils';

const INPUT_CONFIG_TYPES = [
  ComponentConfigType.Variable,
  ComponentConfigType.Value,
  ComponentConfigType.JsonLogic,
];

const INPUT_TOOLTIPS = [
  { key: ComponentConfigType.Variable, value: BASIC_VARIABLE_TOOLTIP },
  { key: ComponentConfigType.Value, value: BASIC_VALUE_TOOLTIP },
  { key: ComponentConfigType.JsonLogic, value: BASIC_JSON_LOGIC_TOOLTIP },
];

const SELECT_TOOLTIPS = [
  { key: ComponentConfigType.Variable, value: BASIC_VARIABLE_TOOLTIP },
  { key: ComponentConfigType.Value, value: BASIC_SELECT_VALUE_TOOLTIP },
  { key: ComponentConfigType.JsonLogic, value: BASIC_JSON_LOGIC_TOOLTIP },
];

const SWITCH_TOOLTIPS = [
  { key: ComponentConfigType.Variable, value: BASIC_VARIABLE_TOOLTIP },
  { key: ComponentConfigType.Value, value: BASIC_SWITCH_VALUE_TOOLTIP },
  { key: ComponentConfigType.JsonLogic, value: BASIC_JSON_LOGIC_TOOLTIP },
];

export const config: IConfig = {
  component: ComponentType.FileViewer,
  configGroups: [
    {
      title: 'Component Properties',
      items: [
        {
          name: 'file',
          label: 'File',
          valueComponent: { type: 'Input' },
          configTypes: [
            ComponentConfigType.Variable,
            ComponentConfigType.JsonLogic,
          ],
          tooltips: INPUT_TOOLTIPS,
        },
        {
          name: 'modelInstanceId',
          label: 'Model Instance ID',
          valueComponent: { type: 'Input' },
          configTypes: [
            ComponentConfigType.Variable,
            ComponentConfigType.Value,
            ComponentConfigType.JsonLogic,
          ],
          tooltips: INPUT_TOOLTIPS,
        },
        {
          name: 'displayMode',
          label: 'Display Mode',
          valueComponent: {
            type: 'Select',
            options: [
              { label: 'Modal', value: 'modal' },
              { label: 'Drawer', value: 'drawer' },
            ],
            defaultValue: 'modal',
          },
          configTypes: [
            ComponentConfigType.Variable,
            ComponentConfigType.Value,
            ComponentConfigType.JsonLogic,
          ],
          tooltips: SELECT_TOOLTIPS,
        },
        {
          name: 'width',
          label: 'Width',
          valueComponent: { type: 'Input' },
          configTypes: [
            ComponentConfigType.Variable,
            ComponentConfigType.Value,
            ComponentConfigType.JsonLogic,
          ],
          tooltips: INPUT_TOOLTIPS,
        },
        {
          name: 'visible',
          label: 'Visible',
          valueComponent: { type: 'Switch' },
          configTypes: [
            ComponentConfigType.Variable,
            ComponentConfigType.Value,
            ComponentConfigType.JsonLogic,
          ],
          tooltips: SWITCH_TOOLTIPS,
        },
        {
          name: 'downloadButtonText',
          label: 'Download Button Text',
          valueComponent: { type: 'Input' },
          configTypes: [
            ComponentConfigType.Variable,
            ComponentConfigType.Value,
            ComponentConfigType.JsonLogic,
            ComponentConfigType.Translate,
          ],
          tooltips: INPUT_TOOLTIPS,
        },
        {
          name: 'previewButtonNotAvailableText',
          label: 'Preview Not Available Text',
          valueComponent: { type: 'Input' },
          configTypes: [
            ComponentConfigType.Variable,
            ComponentConfigType.Value,
            ComponentConfigType.JsonLogic,
            ComponentConfigType.Translate,
          ],
          tooltips: INPUT_TOOLTIPS,
        },
      ],
    },
    {
      title: 'Trigger Button',
      items: [
        {
          name: 'label',
          label: 'Label',
          valueComponent: { type: 'Input' },
          configTypes: [
            ComponentConfigType.Variable,
            ComponentConfigType.Value,
            ComponentConfigType.JsonLogic,
            ComponentConfigType.Translate,
          ],
          tooltips: [
            ...INPUT_TOOLTIPS,
            {
              key: ComponentConfigType.Translate,
              value: BASIC_TANSLATE_TOOLTIP,
            },
          ],
        },
        {
          name: 'type',
          label: 'Type',
          valueComponent: {
            type: 'Select',
            options: [
              { label: 'Primary', value: 'primary' },
              { label: 'Link', value: 'link' },
              { label: 'Text', value: 'text' },
              { label: 'Default', value: 'default' },
              { label: 'Dashed', value: 'dashed' },
            ],
            defaultValue: 'primary',
          },
          configTypes: INPUT_CONFIG_TYPES,
          tooltips: SELECT_TOOLTIPS,
        },
        {
          name: 'variant',
          label: 'Variant',
          valueComponent: {
            type: 'Select',
            options: [
              { label: 'Solid', value: 'solid' },
              { label: 'Outlined', value: 'outlined' },
              { label: 'Dashed', value: 'dashed' },
              { label: 'Filled', value: 'filled' },
              { label: 'Link', value: 'link' },
              { label: 'Text', value: 'text' },
            ],
            defaultValue: 'outlined',
          },
          configTypes: INPUT_CONFIG_TYPES,
          tooltips: SELECT_TOOLTIPS,
        },
        {
          name: 'size',
          label: 'Size',
          valueComponent: {
            type: 'Select',
            options: [
              { label: 'Small', value: 'small' },
              { label: 'Middle', value: 'middle' },
              { label: 'Large', value: 'large' },
            ],
            defaultValue: 'middle',
          },
          configTypes: INPUT_CONFIG_TYPES,
          tooltips: SELECT_TOOLTIPS,
        },
        {
          name: 'icon',
          label: 'Icon',
          valueComponent: {
            type: 'Input',
            placeholder: 'e.g., PlusOutlined, EditOutlined',
          },
          configTypes: [
            ComponentConfigType.Variable,
            ComponentConfigType.Value,
            ComponentConfigType.JsonLogic,
          ],
          tooltips: [
            {
              key: ComponentConfigType.Variable,
              value: BASIC_VARIABLE_TOOLTIP,
            },
            { key: ComponentConfigType.Value, value: BASIC_VALUE_TOOLTIP },
            {
              key: ComponentConfigType.JsonLogic,
              value: BASIC_JSON_LOGIC_TOOLTIP,
            },
          ],
        },
        {
          name: 'disabled',
          label: 'Disabled',
          valueComponent: { type: 'Switch' },
          configTypes: INPUT_CONFIG_TYPES,
          tooltips: SWITCH_TOOLTIPS,
        },
        {
          name: 'loading',
          label: 'Loading',
          valueComponent: { type: 'Switch' },
          configTypes: INPUT_CONFIG_TYPES,
          tooltips: SWITCH_TOOLTIPS,
        },
      ],
    },
    {
      title: 'Component Styles',
      component: 'CssSettings',
    },
  ],
};
